import './euras-bank.scss';

import { Modal } from 'antd';
import Collapse from 'antd/lib/collapse';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { Button, DocumentController } from '@/components/Shared/ui';
import { useAppDispatch, useAppSelector } from '@/store';
import { onSetSelectedBank } from '@/store/financingStep.slice';

import { FILE_NAMES } from '../../constants';
import {
  EurasInitialPayment,
  EurasMarriageDocuments,
  EurasOfflineDocuments,
  GrnFactoryCheck
} from '../../entities';
import useInstructionsText from '../../hooks/useInstructionsText';
import type { SendDocumentsHandler } from '../../widgets';
import { fileDict, type fileDictType } from './constants/files.dicts';
import useEurasForm from './hooks/useEurasForm';
import useModerationDocuments from './hooks/useModerationDocuments';
import useSubmitAction from './hooks/useSubmitAction';

const hideContinueStatuses = [
  'financed',
  'waiting_of_financing',
  'refund_in_progress',
  'refunded',
  'cancelled',
  'declined',
  'error'
];

interface IProps {
  isSubmitLoading: boolean;
  onSubmit: SendDocumentsHandler;
}

const EurasBankV2: React.FC<IProps> = ({
  isSubmitLoading,
  onSubmit
}: IProps) => {
  const dispatch = useAppDispatch();

  const {
    userAccesses: {
      autoCredit: { canConfirmInitialPayment, canSendDocuments }
    }
  } = useAppSelector((state) => state.userInfo);
  const { banksOffers } = useAppSelector((state) => state.offerStep);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  // TODO Вернуть когда евраз запустится для всех пользователей
  // const { tsModalValues } = useAppSelector((state) => state.newOrder);
  const { selectedBank, isEuBankOfflineProcess, isGrnFactoryCheckPassed } =
    useAppSelector((state) => state.financingStep);

  const { documentsOnModeration } = useModerationDocuments();
  const { methods, files, filesOnModeration, attachedCollapseFiles } =
    useEurasForm({
      documentsOnModeration
    });

  useInstructionsText({
    extrajudicialEnforcementUuid: files?.extrajudicialEnforcement?.uuid
  });

  const [confirmOpened, setConfirmOpened] = useState(false);

  const shouldShowContinue = hideContinueStatuses.includes(
    selectedBank?.status.code || ''
  );
  const isInitialPaymentConfirmation =
    selectedBank?.status.code === 'mobile_client';
  const isOnRework = selectedBank?.status.code === 'on_rework';
  const isBitrixConfirmation =
    selectedBank?.status.code === 'waiting_for_docs_confirmation';
  // TODO Вернуть когда евраз запустится для всех пользователей
  // const isSubmitFrozen =
  //   (!tsModalValues.licensePlate || !tsModalValues.registerNum) &&
  //   selectedBank?.status.code === 'vehicle_registration';

  const handleClose = () => setConfirmOpened(false);

  const { isSubmitDisabled, confirmBitrixLoading, handleSubmit } =
    useSubmitAction({
      methods,
      files,
      onSubmit,
      handleClose,
      documentsOnModeration
    });

  useEffect(() => {
    if (!selectedBank?.id || selectedBank.bank.name !== 'eu_bank_v2') return;
    // Отслеживание изменения статуса по предложению банка
    const offer = banksOffers.find((offer) => offer.id === selectedBank?.id);
    if (offer) dispatch(onSetSelectedBank(offer));
  }, [banksOffers]);

  return (
    <>
      {documentsOnModeration.length > 0 && (
        <h2 className="euras-bank-v2__moderation-title">
          Документы для переотправки:
        </h2>
      )}

      <FormProvider {...methods}>
        <form
          className="financing-step__form"
          onSubmit={
            isInitialPaymentConfirmation
              ? async (e) => {
                  const isValid = await methods.trigger();
                  e.preventDefault();
                  if (isValid) setConfirmOpened(true);
                }
              : methods.handleSubmit(handleSubmit)
          }
        >
          <EurasInitialPayment
            files={files}
            filesOnModeration={filesOnModeration}
            onSubmit={onSubmit}
          />

          {files?.techPassport && (
            <DocumentController
              fileLink={files.techPassport.uuid}
              title={
                FILE_NAMES[files.techPassport.type as keyof typeof FILE_NAMES]
              }
              downloadable
            />
          )}

          {files?.guaranteeLetter && !isOnRework && (
            <>
              <DocumentController
                fileLink={files.guaranteeLetter.uuid}
                title={fileDict.guaranteeLetter}
                downloadable
              />
              <GrnFactoryCheck
                grnFileLink={files.guaranteeLetter.link}
                grnFileType={files.guaranteeLetter.type}
              />
            </>
          )}

          {!isInitialPaymentConfirmation && (
            <>
              {(!isBitrixConfirmation || isEuBankOfflineProcess) && (
                <EurasMarriageDocuments
                  extrajudicialEnforcement={
                    orderApp?.isOnlineRequest
                      ? files?.spouseConsent
                      : files?.extrajudicialEnforcement
                  }
                  marriageContract={files?.marriageContract}
                  spouseConsent={files?.spouseConsent}
                  filesOnModeration={filesOnModeration}
                  handleSubmit={handleSubmit}
                  documentsOnModeration={documentsOnModeration}
                />
              )}

              <EurasOfflineDocuments
                files={{
                  certificateOrigin: files?.certificateOrigin
                }}
                filesOnModeration={
                  filesOnModeration
                    ? { certificateOrigin: filesOnModeration.certificateOrigin }
                    : undefined
                }
              />
            </>
          )}

          {selectedBank?.status.code === 'financed' && (
            <>
              {files?.paymentOrderEubank && (
                <DocumentController
                  fileLink={files.paymentOrderEubank.uuid}
                  title={fileDict.paymentOrderEubank}
                  downloadable
                />
              )}
              {files?.loanApprovalNoticeEubank && (
                <DocumentController
                  fileLink={files.loanApprovalNoticeEubank.uuid}
                  title={fileDict.loanApprovalNoticeEubank}
                  downloadable
                />
              )}
            </>
          )}

          {isOnRework && (
            <Collapse
              className="financing-step__field"
              items={[
                {
                  key: selectedBank?.id + (selectedBank?.extId || ''),
                  label: 'Прикрепленные файлы',
                  children: attachedCollapseFiles.length ? (
                    attachedCollapseFiles.map((data) => (
                      <DocumentController
                        key={data.uuid}
                        fileLink={data.uuid}
                        title={fileDict[data.type as fileDictType] || data.type}
                        downloadable
                        wrapperClassName="euras-bank-v2__collapsed-document"
                      />
                    ))
                  ) : (
                    <p className="financing-step__no-documents">
                      Нет прикрепленных файлов
                    </p>
                  )
                }
              ]}
            />
          )}

          {!shouldShowContinue && (
            <>
              <Button
                variant="filled-red"
                // TODO Вернуть когда евраз запустится для всех пользователей
                // disabled={isSubmitDisabled || isSubmitFrozen}
                disabled={
                  isSubmitDisabled ||
                  !isGrnFactoryCheckPassed ||
                  (isInitialPaymentConfirmation
                    ? !canConfirmInitialPayment
                    : !canSendDocuments)
                }
                isLoading={isSubmitLoading || confirmBitrixLoading}
                fullWidth
              >
                {isInitialPaymentConfirmation
                  ? 'Подтвердить оплату ПВ'
                  : 'Отправить документы'}
              </Button>
            </>
          )}
        </form>

        <Modal
          centered
          mask={{ closable: true }}
          className="euras-bank-confirm-modal"
          title={`Вы подтверждаете что клиент оплатил ПВ в размере: ${selectedBank?.initialPayment} ₸`}
          open={confirmOpened}
          onCancel={handleClose}
          footer={null}
        >
          <div className="euras-bank-confirm-modal__actions">
            <Button type="button" onClick={handleClose}>
              Отмена
            </Button>
            <Button
              type="button"
              variant="filled-red"
              isLoading={isSubmitLoading}
              onClick={handleSubmit}
            >
              Подтвердить
            </Button>
          </div>
        </Modal>
      </FormProvider>
    </>
  );
};

export default EurasBankV2;
